import { useEffect, useMemo } from 'react';
import { useUserStore } from 'src/store/user-store';
import { useAvailableSites } from './api/use-available-sites';
import { useAvailableTenants } from './api/use-available-tenants';

export const useSetInitialOrganizations = (isLoggedIn: boolean = true) => {
  const directoryToken = useUserStore(state => state.directoryToken);
  const setDirectoryToken = useUserStore(state => state.setDirectoryToken);
  const setAdminToken = useUserStore(state => state.setAdminToken);
  const setRole = useUserStore(state => state.setRole);
  const viewContext = useUserStore(state => state.viewContext);
  const setIsSSO = useUserStore(state => state.setIsSSO);

  const { data: availableTenantsData, isLoading: isLoadingAvailableTenants } =
    useAvailableTenants(isLoggedIn);

  const memoizedAvailableTenantsData = useMemo(
    () => availableTenantsData,
    [availableTenantsData],
  );

  const { data: availableSitesData, isLoading: isLoadingAvailableSites } =
    useAvailableSites(isLoggedIn);

  const memoizedAvailableSitesData = useMemo(
    () => availableSitesData,
    [availableSitesData],
  );

  useEffect(() => {
    const hasAvailableTenants =
      memoizedAvailableTenantsData &&
      memoizedAvailableTenantsData?.resources.length >= 1;

    if (viewContext === 'tenant') {
      if (hasAvailableTenants && !directoryToken) {
        setDirectoryToken(memoizedAvailableTenantsData?.resources?.[0]?.token);
      }
    }

    const hasAvailableSites =
      memoizedAvailableSitesData &&
      memoizedAvailableSitesData?.resources.length >= 1;

    if (viewContext === 'site') {
      if (hasAvailableSites && !directoryToken) {
        setDirectoryToken(memoizedAvailableSitesData?.resources?.[0]?.token);
      }
    }
  }, [
    directoryToken,
    memoizedAvailableSitesData,
    memoizedAvailableTenantsData,
    setDirectoryToken,
    viewContext,
  ]);

  useEffect(() => {
    const hasAvailableTenants =
      memoizedAvailableTenantsData &&
      memoizedAvailableTenantsData?.resources.length >= 1;

    if (viewContext === 'tenant') {
      if (hasAvailableTenants && isLoggedIn && !isLoadingAvailableTenants) {
        setRole(memoizedAvailableTenantsData?.resources?.[0]?.role);
        setAdminToken(
          memoizedAvailableTenantsData?.resources?.[0]?.tenantAdminToken,
        );
        setIsSSO(memoizedAvailableTenantsData?.resources?.[0]?.isSSO ?? false);
      }
    }

    const hasAvailableSites =
      memoizedAvailableSitesData &&
      memoizedAvailableSitesData?.resources.length >= 1;

    if (viewContext === 'site') {
      if (hasAvailableSites && isLoggedIn && !isLoadingAvailableSites) {
        setRole(memoizedAvailableSitesData?.resources?.[0]?.role);
        setAdminToken(
          memoizedAvailableSitesData?.resources?.[0]?.siteAdminToken,
        );
      }
    }
  }, [
    isLoadingAvailableSites,
    isLoadingAvailableTenants,
    isLoggedIn,
    memoizedAvailableSitesData,
    memoizedAvailableTenantsData,
    setAdminToken,
    setIsSSO,
    setRole,
    viewContext,
  ]);

  return {
    isLoadingAvailableTenants,
    isLoadingAvailableSites,
  };
};
